import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Globe } from "lucide-react";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import { toast } from "sonner";

const TABS = [
  { key: "general", label: "General" },
  { key: "hero", label: "Hero" },
  { key: "marquee", label: "Brand Marquee" },
  { key: "trust_badges", label: "Trust Badges" },
  { key: "categories", label: "Categories" },
  { key: "sections", label: "Section Labels" },
  { key: "footer", label: "Footer" },
];

const ICON_OPTIONS = ["Truck", "ShieldCheck", "Banknote", "Headphones", "Star", "Award", "Clock", "Phone"];

// Keys that support Arabic variants (via `<key>_ar`).
const AR_TRANSLATABLE = {
  hero: [
    "eyebrow", "headline_line1", "headline_line2", "headline_line3",
    "subheadline", "cta_primary_label", "cta_secondary_label",
  ],
  sections: [
    "categories_eyebrow", "categories_title",
    "deals_eyebrow", "deals_title",
    "featured_eyebrow", "featured_title",
    "cta_strip_text", "cta_strip_button_label",
  ],
  footer: ["about", "operating_in", "tagline_right", "copyright_text"],
  general: ["tagline"],
};

function Field({ label, value, onChange, multiline = false, type = "text", rtl = false }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">{label}</span>
      {multiline ? (
        <textarea
          dir={rtl ? "rtl" : "ltr"}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="mt-1.5 w-full px-3 py-2 rounded-lg bg-secondary focus:outline-none focus:ring-2 focus:ring-electric/30 text-sm"
          style={rtl ? { fontFamily: '"Cairo","Tajawal",sans-serif' } : undefined}
        />
      ) : (
        <input
          dir={rtl ? "rtl" : "ltr"}
          type={type}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1.5 w-full h-11 px-3 rounded-lg bg-secondary focus:outline-none focus:ring-2 focus:ring-electric/30 text-sm"
          style={rtl ? { fontFamily: '"Cairo","Tajawal",sans-serif' } : undefined}
        />
      )}
    </label>
  );
}

export default function AdminSettings() {
  const { settings, update, refresh } = useSiteSettings();
  const [tab, setTab] = useState("general");
  const [editLang, setEditLang] = useState("en"); // "en" | "ar"
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(JSON.parse(JSON.stringify(settings || {})));
  }, [settings]);

  if (!draft) return <div className="p-8 font-mono text-slate-500">Loading…</div>;

  // Translate key path based on lang. For translatable fields, AR reads/writes `${key}_ar`.
  const arKey = (section, key) => {
    const list = AR_TRANSLATABLE[section] || [];
    return editLang === "ar" && list.includes(key) ? `${key}_ar` : key;
  };

  const setKey = (section, key, value) => {
    const k = arKey(section, key);
    setDraft((prev) => ({ ...prev, [section]: { ...(prev[section] || {}), [k]: value } }));
  };
  const getKey = (section, key) => {
    const k = arKey(section, key);
    return draft[section]?.[k];
  };

  const setArray = (section, idx, key, value) => {
    setDraft((prev) => {
      const arr = [...(prev[section] || [])];
      if (typeof arr[idx] === "string") arr[idx] = value;
      else arr[idx] = { ...arr[idx], [key]: value };
      return { ...prev, [section]: arr };
    });
  };

  const addToArray = (section, item) => {
    setDraft((prev) => ({ ...prev, [section]: [...(prev[section] || []), item] }));
  };

  const removeFromArray = (section, idx) => {
    setDraft((prev) => {
      const arr = [...(prev[section] || [])];
      arr.splice(idx, 1);
      return { ...prev, [section]: arr };
    });
  };

  const setNestedArrayItem = (section, key, idx, prop, value) => {
    setDraft((prev) => {
      const sec = { ...(prev[section] || {}) };
      const arr = [...(sec[key] || [])];
      arr[idx] = { ...arr[idx], [prop]: value };
      sec[key] = arr;
      return { ...prev, [section]: sec };
    });
  };

  const addNested = (section, key, item) => {
    setDraft((prev) => {
      const sec = { ...(prev[section] || {}) };
      sec[key] = [...(sec[key] || []), item];
      return { ...prev, [section]: sec };
    });
  };

  const removeNested = (section, key, idx) => {
    setDraft((prev) => {
      const sec = { ...(prev[section] || {}) };
      const arr = [...(sec[key] || [])];
      arr.splice(idx, 1);
      sec[key] = arr;
      return { ...prev, [section]: sec };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await update({ [tab]: draft[tab] });
      toast.success("Saved · live on the storefront");
      await refresh();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const TabBtn = ({ k, label }) => (
    <button
      onClick={() => setTab(k)}
      data-testid={`settings-tab-${k}`}
      className={`shrink-0 h-9 px-4 rounded-full text-[11px] font-mono uppercase tracking-[0.18em] transition-all ${
        tab === k ? "bg-ink text-white" : "bg-secondary text-slate-600 hover:bg-ink hover:text-white"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div data-testid="admin-settings">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500">CMS</div>
          <h1 className="mt-2 font-display font-extrabold text-4xl tracking-tight">Site Settings</h1>
          <p className="mt-1 text-sm text-slate-500">Edit anything on your storefront — changes go live instantly.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-0.5 bg-secondary rounded-full p-1">
            <button
              type="button"
              onClick={() => setEditLang("en")}
              data-testid="settings-lang-en"
              className={`h-8 px-3 rounded-full text-[11px] font-mono uppercase tracking-[0.18em] transition-colors ${
                editLang === "en" ? "bg-ink text-white" : "text-slate-500 hover:text-ink"
              }`}
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => setEditLang("ar")}
              data-testid="settings-lang-ar"
              className={`h-8 px-3 rounded-full text-[11px] font-mono uppercase tracking-[0.18em] transition-colors ${
                editLang === "ar" ? "bg-ink text-white" : "text-slate-500 hover:text-ink"
              }`}
            >
              AR
            </button>
          </div>
          <button
            onClick={save}
            disabled={saving}
            data-testid="settings-save"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-ink text-white hover:bg-electric disabled:opacity-50 text-xs font-mono uppercase tracking-[0.2em]"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="mt-3 text-[11px] font-mono uppercase tracking-widest text-slate-500">
        {editLang === "ar"
          ? "Editing Arabic content · fields fall back to English when empty."
          : "Editing English content. Switch to AR to author the Arabic version."}
      </div>

      <div className="mt-6 flex items-center gap-2 overflow-x-auto pb-2">
        {TABS.map((t) => <TabBtn key={t.key} k={t.key} label={t.label} />)}
      </div>

      <div className="mt-6 bg-white rounded-2xl shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)] p-6 sm:p-8">
        {/* GENERAL */}
        {tab === "general" && (
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Site name" value={draft.general?.site_name} onChange={(v) => setDraft((p) => ({ ...p, general: { ...(p.general || {}), site_name: v } }))} />
            <Field label={editLang === "ar" ? "Tagline (AR)" : "Tagline"} rtl={editLang === "ar"} value={getKey("general", "tagline")} onChange={(v) => setKey("general", "tagline", v)} />
            <Field label="Logo URL" value={draft.general?.logo_url} onChange={(v) => setDraft((p) => ({ ...p, general: { ...(p.general || {}), logo_url: v } }))} />
            <Field label="WhatsApp number (no +/0)" value={draft.general?.whatsapp_number} onChange={(v) => setDraft((p) => ({ ...p, general: { ...(p.general || {}), whatsapp_number: v } }))} />
            <Field label="Phone (display)" value={draft.general?.phone} onChange={(v) => setDraft((p) => ({ ...p, general: { ...(p.general || {}), phone: v } }))} />
            <Field label="Email" value={draft.general?.email} onChange={(v) => setDraft((p) => ({ ...p, general: { ...(p.general || {}), email: v } }))} />
            <Field label="Currency symbol" value={draft.general?.currency_symbol} onChange={(v) => setDraft((p) => ({ ...p, general: { ...(p.general || {}), currency_symbol: v } }))} />
            {draft.general?.logo_url && (
              <div className="sm:col-span-2 flex items-center gap-3">
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Logo preview:</span>
                <img src={draft.general.logo_url} alt="logo" className="w-12 h-12 object-contain bg-secondary rounded-lg p-1" />
              </div>
            )}
          </div>
        )}

        {/* HERO */}
        {tab === "hero" && (
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={`Eyebrow text${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("hero", "eyebrow")} onChange={(v) => setKey("hero", "eyebrow", v)} />
              <Field label={`Headline line 1${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("hero", "headline_line1")} onChange={(v) => setKey("hero", "headline_line1", v)} />
              <Field label={`Headline line 2 (accent)${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("hero", "headline_line2")} onChange={(v) => setKey("hero", "headline_line2", v)} />
              <Field label={`Headline line 3${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("hero", "headline_line3")} onChange={(v) => setKey("hero", "headline_line3", v)} />
            </div>
            <Field label={`Subheadline${editLang === "ar" ? " (AR)" : ""}`} multiline rtl={editLang === "ar"} value={getKey("hero", "subheadline")} onChange={(v) => setKey("hero", "subheadline", v)} />
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={`Primary CTA label${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("hero", "cta_primary_label")} onChange={(v) => setKey("hero", "cta_primary_label", v)} />
              <Field label="Primary CTA link" value={draft.hero?.cta_primary_link} onChange={(v) => setDraft((p) => ({ ...p, hero: { ...(p.hero || {}), cta_primary_link: v } }))} />
              <Field label={`Secondary CTA label${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("hero", "cta_secondary_label")} onChange={(v) => setKey("hero", "cta_secondary_label", v)} />
              <Field label="Secondary CTA link" value={draft.hero?.cta_secondary_link} onChange={(v) => setDraft((p) => ({ ...p, hero: { ...(p.hero || {}), cta_secondary_link: v } }))} />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Stats (3 max for nice grid)</span>
                <button onClick={() => addNested("hero", "stats", { value: "", label: "" })} className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-secondary hover:bg-ink hover:text-white text-[11px] font-mono uppercase">
                  <Plus size={12} /> Add
                </button>
              </div>
              <div className="space-y-2">
                {(draft.hero?.stats || []).map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input value={s.value || ""} onChange={(e) => setNestedArrayItem("hero", "stats", i, "value", e.target.value)} placeholder="12+" className="col-span-3 h-10 px-3 rounded-lg bg-secondary text-sm focus:outline-none focus:ring-2 focus:ring-electric/30" />
                    <input value={s.label || ""} onChange={(e) => setNestedArrayItem("hero", "stats", i, "label", e.target.value)} placeholder="Years on market" className="col-span-8 h-10 px-3 rounded-lg bg-secondary text-sm focus:outline-none focus:ring-2 focus:ring-electric/30" />
                    <button onClick={() => removeNested("hero", "stats", i)} className="col-span-1 w-9 h-9 rounded-full bg-secondary hover:bg-destructive hover:text-white grid place-items-center"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* MARQUEE */}
        {tab === "marquee" && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-slate-600">Words/brands shown in the moving stripe under the hero.</span>
              <button onClick={() => addToArray("marquee", "")} className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-secondary hover:bg-ink hover:text-white text-[11px] font-mono uppercase">
                <Plus size={12} /> Add
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {(draft.marquee || []).map((m, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={m} onChange={(e) => setArray("marquee", i, null, e.target.value)} className="flex-1 h-10 px-3 rounded-lg bg-secondary text-sm font-mono uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-electric/30" />
                  <button onClick={() => removeFromArray("marquee", i)} className="w-9 h-9 rounded-full bg-secondary hover:bg-destructive hover:text-white grid place-items-center"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TRUST BADGES */}
        {tab === "trust_badges" && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-slate-600">
                Trust strip below the marquee. {editLang === "ar" && "· Editing Arabic version"}
              </span>
              <button onClick={() => addToArray("trust_badges", { icon: "Truck", label: "", sub: "" })} className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-secondary hover:bg-ink hover:text-white text-[11px] font-mono uppercase">
                <Plus size={12} /> Add
              </button>
            </div>
            <div className="space-y-3">
              {(draft.trust_badges || []).map((b, i) => {
                const labelKey = editLang === "ar" ? "label_ar" : "label";
                const subKey = editLang === "ar" ? "sub_ar" : "sub";
                return (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <select value={b.icon || "Truck"} onChange={(e) => setArray("trust_badges", i, "icon", e.target.value)} className="col-span-3 h-10 px-2 rounded-lg bg-secondary text-sm focus:outline-none focus:ring-2 focus:ring-electric/30">
                      {ICON_OPTIONS.map((io) => <option key={io} value={io}>{io}</option>)}
                    </select>
                    <input dir={editLang === "ar" ? "rtl" : "ltr"} value={b[labelKey] || ""} onChange={(e) => setArray("trust_badges", i, labelKey, e.target.value)} placeholder={editLang === "ar" ? "توصيل مجاني" : "Free Delivery"} className="col-span-4 h-10 px-3 rounded-lg bg-secondary text-sm focus:outline-none focus:ring-2 focus:ring-electric/30" style={editLang === "ar" ? { fontFamily: '"Cairo","Tajawal",sans-serif' } : undefined} />
                    <input dir={editLang === "ar" ? "rtl" : "ltr"} value={b[subKey] || ""} onChange={(e) => setArray("trust_badges", i, subKey, e.target.value)} placeholder={editLang === "ar" ? "للطلبات فوق 100 د.أ" : "Over 100 JOD"} className="col-span-4 h-10 px-3 rounded-lg bg-secondary text-sm focus:outline-none focus:ring-2 focus:ring-electric/30" style={editLang === "ar" ? { fontFamily: '"Cairo","Tajawal",sans-serif' } : undefined} />
                    <button onClick={() => removeFromArray("trust_badges", i)} className="col-span-1 w-9 h-9 rounded-full bg-secondary hover:bg-destructive hover:text-white grid place-items-center"><Trash2 size={13} /></button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* CATEGORIES */}
        {tab === "categories" && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-slate-600">
                Cards in the "Built for every space" section. {editLang === "ar" && "· Editing Arabic version"}
              </span>
              <button onClick={() => addToArray("categories", { key: "", label: "", image: "" })} className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-secondary hover:bg-ink hover:text-white text-[11px] font-mono uppercase">
                <Plus size={12} /> Add
              </button>
            </div>
            <div className="space-y-3">
              {(draft.categories || []).map((c, i) => {
                const labelKey = editLang === "ar" ? "label_ar" : "label";
                return (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    {c.image && <img src={c.image} alt="" className="col-span-1 w-12 h-12 object-contain rounded-lg bg-secondary p-1" />}
                    <input value={c.key || ""} onChange={(e) => setArray("categories", i, "key", e.target.value)} placeholder="split" className="col-span-2 h-10 px-3 rounded-lg bg-secondary text-sm font-mono focus:outline-none focus:ring-2 focus:ring-electric/30" />
                    <input dir={editLang === "ar" ? "rtl" : "ltr"} value={c[labelKey] || ""} onChange={(e) => setArray("categories", i, labelKey, e.target.value)} placeholder={editLang === "ar" ? "سبليت" : "Split AC"} className="col-span-3 h-10 px-3 rounded-lg bg-secondary text-sm focus:outline-none focus:ring-2 focus:ring-electric/30" style={editLang === "ar" ? { fontFamily: '"Cairo","Tajawal",sans-serif' } : undefined} />
                    <input value={c.image || ""} onChange={(e) => setArray("categories", i, "image", e.target.value)} placeholder="https://…" className="col-span-5 h-10 px-3 rounded-lg bg-secondary text-sm font-mono focus:outline-none focus:ring-2 focus:ring-electric/30" />
                    <button onClick={() => removeFromArray("categories", i)} className="col-span-1 w-9 h-9 rounded-full bg-secondary hover:bg-destructive hover:text-white grid place-items-center"><Trash2 size={13} /></button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* SECTIONS */}
        {tab === "sections" && (
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label={`Categories eyebrow${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("sections", "categories_eyebrow")} onChange={(v) => setKey("sections", "categories_eyebrow", v)} />
            <Field label={`Categories title${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("sections", "categories_title")} onChange={(v) => setKey("sections", "categories_title", v)} />
            <Field label={`Deals eyebrow${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("sections", "deals_eyebrow")} onChange={(v) => setKey("sections", "deals_eyebrow", v)} />
            <Field label={`Deals title${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("sections", "deals_title")} onChange={(v) => setKey("sections", "deals_title", v)} />
            <Field label={`Featured eyebrow${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("sections", "featured_eyebrow")} onChange={(v) => setKey("sections", "featured_eyebrow", v)} />
            <Field label={`Featured title${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("sections", "featured_title")} onChange={(v) => setKey("sections", "featured_title", v)} />
            <Field label={`CTA strip text${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("sections", "cta_strip_text")} onChange={(v) => setKey("sections", "cta_strip_text", v)} />
            <Field label={`CTA strip button${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("sections", "cta_strip_button_label")} onChange={(v) => setKey("sections", "cta_strip_button_label", v)} />
          </div>
        )}

        {/* FOOTER */}
        {tab === "footer" && (
          <div className="space-y-5">
            <Field label={`About paragraph${editLang === "ar" ? " (AR)" : ""}`} multiline rtl={editLang === "ar"} value={getKey("footer", "about")} onChange={(v) => setKey("footer", "about", v)} />
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={`Operating cities${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("footer", "operating_in")} onChange={(v) => setKey("footer", "operating_in", v)} />
              <Field label={`Tagline (right side)${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("footer", "tagline_right")} onChange={(v) => setKey("footer", "tagline_right", v)} />
              <Field label={`Copyright text${editLang === "ar" ? " (AR)" : ""}`} rtl={editLang === "ar"} value={getKey("footer", "copyright_text")} onChange={(v) => setKey("footer", "copyright_text", v)} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
